import {
  type ActiveQuest,
  biomeAt,
  emptyCareer,
  getItem,
  getQuest,
  isQuestComplete,
  isQuestNpc,
  MOBS,
  MobIdSchema,
  type NpcIdentity,
  type ObjectiveProgress,
  offersAt,
  type Quest,
  type QuestFacts,
  type QuestMarker,
  type QuestNpc,
  QUEST_NPC_DEFAULT_ZONE,
  QUESTS,
  type QuestsEvent,
  type ZoneDefinition,
} from '@shefira/shared';

/**
 * The player's journal as the server last sent it (ADR 0047), and what the
 * interface reads from it: the marker over each townsperson, what their
 * dialogue holds, and where the map should point. Nothing here decides a
 * quest: the counts are the server's, the rules only sort them for display.
 */

export interface QuestEntry {
  quest: Quest;
  progress: ObjectiveProgress[];
  flags: number[];
  complete: boolean;
}

/** What a townsperson has to say, in the order the dialogue offers it. */
export interface NpcAgenda {
  /** Quests finished that they take back. */
  ready: QuestEntry[];
  /** Words they have to say for a quest under way (the smith's greeting). */
  talks: { entry: QuestEntry; index: number }[];
  /** Quests they offer now. */
  offers: Quest[];
  /** Quests under way they take back, not finished yet. */
  pending: QuestEntry[];
  /** Quests they will offer at a higher level. */
  upcoming: Quest[];
}

/** A point of the map a quest leads to: a townsperson, a place, a camp, a dungeon's gate. */
export interface QuestTarget {
  questId: string;
  x: number;
  y: number;
  /** A place to find (a camp, a named place): the arrow on screen leads there. */
  guide?: boolean;
}

const NO_BAG = { bagSize: 1, bag: [], wardrobe: [], equipped: {}, gold: 0 };

export class QuestLog {
  private active = new Map<string, QuestEntry>();
  private done = new Set<string>();
  private level = 1;
  private version = 0;
  private received = false;
  private readonly markers = new Map<string, QuestMarker | null>();
  private hunted: ReadonlySet<string> | undefined;
  private readonly listeners = new Set<() => void>();

  /** Taken from the server's `quests` message: the whole journal. */
  update(event: QuestsEvent): void {
    this.active = new Map();
    for (const { id, progress, flags } of event.active) {
      const quest = getQuest(id);
      if (quest === undefined) continue;
      this.active.set(id, { quest, progress, flags, complete: isQuestComplete(progress) });
    }
    this.done = new Set(event.done);
    this.received = true;
    this.changed();
  }

  /** Whether the server has sent the journal yet: before it, nothing is known of the story. */
  get known(): boolean {
    return this.received;
  }

  setLevel(level: number): void {
    if (level === this.level) return;
    this.level = level;
    this.changed();
  }

  onChange(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  get stamp(): number {
    return this.version;
  }

  entries(): QuestEntry[] {
    return [...this.active.values()];
  }

  entry(questId: string): QuestEntry | undefined {
    return this.active.get(questId);
  }

  isDone(questId: string): boolean {
    return this.done.has(questId);
  }

  /** Quests turned in, in the order of the data. */
  finished(): Quest[] {
    return QUESTS.filter((quest) => this.done.has(quest.id));
  }

  agendaOf(npc: NpcIdentity): NpcAgenda {
    const facts = this.facts();
    const agenda: NpcAgenda = { ready: [], talks: [], offers: [], pending: [], upcoming: [] };
    for (const entry of this.active.values()) {
      if (isQuestNpc(entry.quest.turnIn, npc)) {
        (entry.complete ? agenda.ready : agenda.pending).push(entry);
      }
      entry.quest.objectives.forEach((objective, index) => {
        if (objective.kind === 'talk' && isQuestNpc(objective.npc, npc)) {
          if (!entry.flags.includes(index)) agenda.talks.push({ entry, index });
        }
      });
    }
    for (const quest of offersAt(npc, facts, true)) {
      (quest.level > this.level ? agenda.upcoming : agenda.offers).push(quest);
    }
    return agenda;
  }

  /** Whether the dialogue has anything for this townsperson: a service's keeper opens his own window otherwise. */
  hasAgenda(npc: NpcIdentity): boolean {
    const agenda = this.agendaOf(npc);
    return (
      agenda.ready.length + agenda.talks.length + agenda.offers.length + agenda.pending.length > 0
    );
  }

  /**
   * The creatures a quest under way still asks for: those to slay, and those
   * whose loot holds an item still to bring (rats for their tails). Their
   * plate wears a mark, so the player knows which ones count.
   */
  huntedMobs(): ReadonlySet<string> {
    if (this.hunted !== undefined) return this.hunted;
    const hunted = new Set<string>();
    for (const entry of this.active.values()) {
      entry.quest.objectives.forEach((objective, index) => {
        const progress = entry.progress[index];
        if (progress !== undefined && progress.value >= progress.target) return;
        if (objective.kind === 'kill') {
          for (const mob of objective.mobs) hunted.add(mob);
        } else if (objective.kind === 'collect') {
          for (const mob of Object.values(MOBS)) {
            if (mob.loot.some((entry) => entry.templateId === objective.item)) hunted.add(mob.id);
          }
        }
      });
    }
    this.hunted = hunted;
    return hunted;
  }

  /** The marker over a townsperson, cached until the journal or the level moves. */
  markerOf(npc: NpcIdentity): QuestMarker | null {
    const key = `${npc.mob}:${npc.biome ?? ''}:${npc.zone ?? QUEST_NPC_DEFAULT_ZONE}`;
    const cached = this.markers.get(key);
    if (cached !== undefined) return cached;
    const agenda = this.agendaOf(npc);
    const marker: QuestMarker | null =
      agenda.ready.length > 0 || agenda.talks.length > 0
        ? 'ready'
        : agenda.offers.length > 0
          ? 'available'
          : agenda.pending.length > 0
            ? 'pending'
            : null;
    this.markers.set(key, marker);
    return marker;
  }

  /**
   * Where each quest under way leads next: the townsperson to talk to, the
   * place to reach, the camps, an elite's lairs, the dungeon's gate of a boss; the one who
   * takes it back once it is done. In the camp (ADR 0066) the arrow leads
   * to its townsfolk and to the player's own rats too: that is where one
   * learns to follow it.
   */
  targets(zone: ZoneDefinition): QuestTarget[] {
    const targets: QuestTarget[] = [];
    const npcTiles = (npc: QuestNpc): { x: number; y: number }[] =>
      (npc.zone ?? QUEST_NPC_DEFAULT_ZONE) !== zone.id
        ? []
        : zone.spawns
            .filter((spawn) => spawn.mob === npc.mob && biomeAt(zone, spawn) === npc.biome)
            .map(({ x, y }) => ({ x, y }));
    /** A townsperson named with a zone of their own stands in a small one: the arrow shows them. */
    const guided = (npc: QuestNpc): boolean => npc.zone !== undefined;
    for (const entry of this.active.values()) {
      const { quest } = entry;
      const add = (tiles: { x: number; y: number }[], guide = false): void => {
        for (const { x, y } of tiles) {
          targets.push({ questId: quest.id, x, y, ...(guide ? { guide } : {}) });
        }
      };
      if (entry.complete) {
        add(npcTiles(quest.turnIn), guided(quest.turnIn));
        continue;
      }
      // Objectives that name where they are done come in order: only the first unmet leads.
      let led = false;
      quest.objectives.forEach((objective, index) => {
        const progress = entry.progress[index];
        if (progress !== undefined && progress.value >= progress.target) return;
        switch (objective.kind) {
          case 'talk':
            add(npcTiles(objective.npc), guided(objective.npc));
            break;
          case 'reach':
            add(
              zone.landmarks.filter((place) => place.name === objective.landmark),
              true,
            );
            break;
          case 'portal':
            add(zone.portals.filter((portal) => portal.id === objective.portal));
            break;
          case 'camp':
            add(
              zone.chests.filter(
                (chest) => chest.kind === 'camp' && chest.biome === objective.biome,
              ),
              true,
            );
            break;
          case 'kill':
            for (const mob of objective.mobs) {
              const parsed = MobIdSchema.safeParse(mob);
              if (!parsed.success) continue;
              const definition = MOBS[parsed.data];
              // A bait lent with the quest (ADR 0047) calls it out by a den: the map points at the dens.
              const bait = quest.grants
                .map((grant) => getItem(grant.templateId)?.bait)
                .find((candidate) => candidate?.summons === mob);
              // The camp's rats, each player's own (ADR 0065), and a quest's
              // quarry at a dragon's mark (ADR 0087), for that quest alone.
              const personal = zone.spawns.filter(
                (spawn) =>
                  spawn.mob === mob &&
                  spawn.personal &&
                  (spawn.during === undefined || spawn.during === quest.id),
              );
              if (personal.length > 0) {
                add(personal, true);
              } else if (bait !== undefined) {
                add(
                  zone.spawns.filter(
                    (spawn) => spawn.mob === bait.den && biomeAt(zone, spawn) === quest.biome,
                  ),
                );
              } else if (definition.rank !== 'ordinary') {
                add(zone.exits.filter((exit) => exit.to === `dungeon-${mob}`));
              } else if (definition.rarity !== 'common') {
                // An elite comes back in one of its lairs (ADR 0023): all of them.
                for (const spawn of zone.spawns.filter((den) => den.mob === mob)) {
                  add(spawn.lairs.length > 0 ? spawn.lairs : [spawn]);
                }
              }
            }
            break;
          case 'wear':
          case 'upgrade': {
            // A piece is bought, or taken to the smith: the map points where
            // it is had (ADR 0078), else at the one who takes it back.
            if (objective.at !== undefined) {
              if (led) break;
              led = true;
            }
            const npc = objective.at ?? quest.turnIn;
            add(npcTiles(npc), guided(npc));
            break;
          }
          case 'collect':
            break;
        }
      });
    }
    // In a zone of its own townsfolk (the camp), with nothing under way to
    // follow, the arrow shows who has the next quest to give.
    if (!targets.some((target) => target.guide === true)) {
      const facts = this.facts();
      for (const quest of QUESTS) {
        if (quest.giver.zone !== zone.id) continue;
        if (!offersAt(quest.giver, facts).some((offer) => offer.id === quest.id)) continue;
        for (const { x, y } of npcTiles(quest.giver)) {
          targets.push({ questId: quest.id, x, y, guide: true });
        }
      }
    }
    return targets;
  }

  /** What the shared rules need to sort offers: only the journal and the level count there. */
  private facts(): QuestFacts {
    const active = new Map<string, ActiveQuest>();
    for (const [id, entry] of this.active) {
      active.set(id, { baseline: { kills: {}, camps: {} }, flags: entry.flags });
    }
    return {
      level: this.level,
      career: emptyCareer(),
      inventory: NO_BAG,
      done: this.done,
      active,
    };
  }

  private changed(): void {
    this.version += 1;
    this.markers.clear();
    this.hunted = undefined;
    for (const listener of this.listeners) listener();
  }
}
